<script setup lang="ts">
import { computed, ref, watch } from "vue";
import {
  UiAside,
  UiButton,
  UiCheckbox,
  UiIcon,
  UiInputField,
  UiRichTextEditor,
  UiSegmentedControl,
  UiText,
  UiTextarea,
  UiTooltip,
  UiUnitNumberField,
  useToaster,
} from "@shopexperts/shared";
import type { CreateProjectQuoteRequest, QuoteInterval } from "@shopexperts/legacy-api";
import type { UseMutationReturnType } from "@tanstack/vue-query";
import ProjectQuoteFeeEstimate from "@/v2Projects/components/ProjectQuoteFeeEstimate.vue";
import { reportProjectUserError } from "@/v2Projects/userErrors";
import { quoteCopy } from "@/v2Projects/utils/quoteCopy";

const open = defineModel<boolean>({ default: false });

const props = withDefaults(defineProps<{
  actingOrgId: number;
  createQuote: UseMutationReturnType<unknown, Error, CreateProjectQuoteRequest, unknown>;
  currencyOptions?: string[];
  defaultCurrency?: string;
}>(), {
  currencyOptions: () => ["USD"],
  defaultCurrency: "USD",
});

const emit = defineEmits<{
  created: [];
}>();

const toaster = useToaster();

const formAmount = ref("");
const formCurrency = ref("USD");
const formInterval = ref<QuoteInterval>("one_time");
const formHasFixedFeeQuantity = ref(false);
const formQuantity = ref("1");
const formDeliveryAfterDays = ref("");
const formIncludesWeekends = ref(false);
const formHasHoursBankExpiration = ref(false);
const formHoursBankExpiresAfterDays = ref("");
const formHoursBankExpirationTerms = ref("");
const formPaymentMode = ref<"internal" | "external">("internal");
const formExternalPaymentUrl = ref("");
const formScope = ref("");

function stripHtml(input: string): string {
  return input
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, " ")
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

const currencyChoices = computed(() => {
  const seen = new Set<string>();
  const values = [props.defaultCurrency, ...props.currencyOptions]
    .map((currency) => currency.trim().toUpperCase())
    .filter((currency) => /^[A-Z]{3}$/.test(currency));

  return values
    .filter((currency) => {
      if (seen.has(currency)) return false;
      seen.add(currency);
      return true;
    })
    .map((currency) => ({ value: currency, label: currency }));
});

const selectedCurrency = computed(() => {
  const currency = formCurrency.value.trim().toUpperCase();
  return /^[A-Z]{3}$/.test(currency) ? currency : "USD";
});

const hasScopeContent = computed(() => stripHtml(formScope.value).length > 0);

const isSubmitting = computed(() => props.createQuote.isPending.value);
const isHourly = computed(() => formInterval.value === "hourly");
const isHoursBank = computed(() => formInterval.value === "hours_bank");
const isFixedFee = computed(() => formInterval.value === "one_time");
const showDeliveryTimeline = computed(() => isFixedFee.value || isHourly.value);
const isExternalPayment = computed(() => formPaymentMode.value === "external");
const parsedAmount = computed(() => Number.parseFloat(formAmount.value));
const parsedQuantity = computed(() => Number.parseInt(formQuantity.value, 10));
const requiresQuantityInput = computed(
  () => isHourly.value || isHoursBank.value || formHasFixedFeeQuantity.value,
);
const submittedQuantity = computed(() =>
  requiresQuantityInput.value ? parsedQuantity.value : 1,
);
const amountLabel = computed(() => {
  const baseLabel = isHoursBank.value
    ? quoteCopy.amountLabels.hours_bank.label
    : isHourly.value
      ? quoteCopy.amountLabels.hourly.label
      : formHasFixedFeeQuantity.value
        ? quoteCopy.amountLabels.one_time.multiple
        : quoteCopy.amountLabels.one_time.single;

  return `${baseLabel} (${selectedCurrency.value})`;
});
const amountHelper = computed(() => {
  if (isHoursBank.value) return quoteCopy.amountLabels.hours_bank.helper;
  if (isHourly.value) return quoteCopy.amountLabels.hourly.helper;
  return formHasFixedFeeQuantity.value
    ? quoteCopy.amountLabels.one_time.helperMultiple
    : quoteCopy.amountLabels.one_time.helperSingle;
});
const quantityLabel = computed(() => quoteCopy.quantityLabels[formInterval.value].label);
const quantityHelper = computed(() => quoteCopy.quantityLabels[formInterval.value].helper);
const deliveryDayUnit = computed<string>({
  get: () => (formIncludesWeekends.value ? "calendar_days" : "business_days"),
  set: (value) => {
    formIncludesWeekends.value = value === "calendar_days";
  },
});

const canSubmit = computed(() => {
  const hasValidAmount = Number.isFinite(parsedAmount.value) && parsedAmount.value > 0;
  const hasValidQuantity =
    !requiresQuantityInput.value ||
    (Number.isInteger(parsedQuantity.value) && parsedQuantity.value > 0);
  return hasValidAmount && hasValidQuantity;
});

const quoteTotalCents = computed(() => {
  if (!canSubmit.value) return null;
  return Math.round(parsedAmount.value * submittedQuantity.value * 100);
});

function resetForm() {
  formAmount.value = "";
  formCurrency.value = props.defaultCurrency.trim().toUpperCase() || "USD";
  formInterval.value = "one_time";
  formHasFixedFeeQuantity.value = false;
  formQuantity.value = "1";
  formDeliveryAfterDays.value = "";
  formIncludesWeekends.value = false;
  formHasHoursBankExpiration.value = false;
  formHoursBankExpiresAfterDays.value = "";
  formHoursBankExpirationTerms.value = "";
  formPaymentMode.value = "internal";
  formExternalPaymentUrl.value = "";
  formScope.value = "";
}

watch(open, (isOpen) => {
  if (isOpen) {
    formCurrency.value = props.defaultCurrency.trim().toUpperCase() || "USD";
    return;
  }

  resetForm();
});

watch(
  () => [props.defaultCurrency, props.currencyOptions] as const,
  () => {
    const available = currencyChoices.value.map((option) => option.value);
    if (!available.includes(formCurrency.value)) {
      formCurrency.value = available[0] ?? "USD";
    }
  },
  { immediate: true },
);

watch(formInterval, (interval) => {
  if (interval === "hourly" || interval === "hours_bank") {
    formHasFixedFeeQuantity.value = false;
    if (formQuantity.value.trim() === "") {
      formQuantity.value = "1";
    }
    if (interval !== "hours_bank") {
      formHasHoursBankExpiration.value = false;
      formHoursBankExpiresAfterDays.value = "";
      formHoursBankExpirationTerms.value = "";
    } else {
      formDeliveryAfterDays.value = "";
      formIncludesWeekends.value = false;
    }
    return;
  }

  formHasHoursBankExpiration.value = false;
  formHoursBankExpiresAfterDays.value = "";
  formHoursBankExpirationTerms.value = "";
  if (!formHasFixedFeeQuantity.value) {
    formQuantity.value = "1";
  }
});

watch(formHasFixedFeeQuantity, (enabled) => {
  if (!enabled && isFixedFee.value) {
    formQuantity.value = "1";
  }
});

watch(formPaymentMode, (mode) => {
  if (mode === "internal") {
    formExternalPaymentUrl.value = "";
  }
});

function closeAside() {
  open.value = false;
}

async function handleSubmit() {
  const amount = parsedAmount.value;
  const quantity = submittedQuantity.value;
  const deliveryAfterDays = showDeliveryTimeline.value && formDeliveryAfterDays.value.trim()
    ? Number.parseInt(formDeliveryAfterDays.value, 10)
    : null;
  const hoursBankExpiresAfterDays = formHoursBankExpiresAfterDays.value.trim()
    ? Number.parseInt(formHoursBankExpiresAfterDays.value, 10)
    : null;
  const hoursBankExpirationTerms = formHoursBankExpirationTerms.value.trim();
  const externalPaymentUrl = formExternalPaymentUrl.value.trim();

  if (!Number.isFinite(amount) || amount <= 0) {
    toaster.create({ title: quoteCopy.validation.amount, type: "error" });
    return;
  }

  if (requiresQuantityInput.value && (!Number.isInteger(quantity) || quantity <= 0)) {
    toaster.create({ title: quoteCopy.validation.quantity, type: "error" });
    return;
  }

  if (
    deliveryAfterDays !== null &&
    (!Number.isInteger(deliveryAfterDays) || deliveryAfterDays < 0)
  ) {
    toaster.create({ title: quoteCopy.validation.deliveryDays, type: "error" });
    return;
  }

  if (
    isHoursBank.value &&
    formHasHoursBankExpiration.value &&
    (hoursBankExpiresAfterDays === null ||
      !Number.isInteger(hoursBankExpiresAfterDays) ||
      hoursBankExpiresAfterDays < 0)
  ) {
    toaster.create({ title: quoteCopy.validation.hoursBankExpirationDays, type: "error" });
    return;
  }

  if (isHoursBank.value && formHasHoursBankExpiration.value && !hoursBankExpirationTerms) {
    toaster.create({ title: quoteCopy.validation.hoursBankExpirationTerms, type: "error" });
    return;
  }

  if (isExternalPayment.value && externalPaymentUrl) {
    try {
      const parsed = new URL(externalPaymentUrl);
      if (!["http:", "https:"].includes(parsed.protocol)) {
        throw new Error("Invalid protocol");
      }
    } catch {
      toaster.create({ title: quoteCopy.validation.paymentLink, type: "error" });
      return;
    }
  }

  try {
    await props.createQuote.mutateAsync({
      acting_org_id: props.actingOrgId,
      amount_cents: Math.round(amount * 100),
      currency: selectedCurrency.value,
      interval: formInterval.value,
      quantity,
      payment_mode: formPaymentMode.value,
      external_payment_url: isExternalPayment.value ? externalPaymentUrl || null : null,
      delivery_after_days: showDeliveryTimeline.value ? deliveryAfterDays : null,
      includes_weekends:
        showDeliveryTimeline.value && deliveryAfterDays !== null ? formIncludesWeekends.value : null,
      hours_bank_expires_after_days:
        isHoursBank.value && formHasHoursBankExpiration.value ? hoursBankExpiresAfterDays : null,
      hours_bank_expiration_terms:
        isHoursBank.value && formHasHoursBankExpiration.value ? hoursBankExpirationTerms : null,
      scope: hasScopeContent.value ? formScope.value.trim() : null,
    });
    toaster.create({ title: quoteCopy.validation.created, type: "success" });
    closeAside();
    emit("created");
  } catch (error) {
    const message = reportProjectUserError("createQuote", error, {
      extra: { actingOrgId: props.actingOrgId },
    });
    toaster.create({ title: message.title, description: message.description, type: "error" });
  }
}
</script>

<template>
  <UiAside v-model="open" position="right" :close-on-overlay-click="false">
    <div class="flex h-full flex-col overflow-y-auto px-4 py-6 md:px-6 md:py-8 gap-8">
      <div class="flex items-start justify-between gap-3">
        <div class="space-y-2">
          <UiText as="h3" variant="h3" text="Offer a quote" class="font-semibold text-gray-900" />
          <UiText
            as="p"
            variant="p"
            text="Define what’s included, how long it will take, and how the client will be billed before they accept the quote."
            class="max-w-md text-gray-600"
          />
        </div>
        <UiButton variant="ghost" shape="square" @click="closeAside">
          <template #before_label>
            <UiIcon name="close" class="h-6 w-6" />
          </template>
        </UiButton>
      </div>

      <div class="flex flex-col gap-8">
        <div>
          <UiText as="label" variant="p-small" :text="quoteCopy.pricingModelLabel" class="mb-2 block  text-gray-700" />
          <UiSegmentedControl v-model="formInterval" :options="quoteCopy.intervalOptions" />
        </div>

        <div>
          <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div :class="requiresQuantityInput ? '' : 'md:col-span-2'">
              <div class="mb-1 flex items-center gap-1.5">
                <UiText as="label" variant="p-small" :text="amountLabel" class="text-gray-700" />
                <UiTooltip :content="amountHelper">
                  <span class="inline-flex cursor-help text-gray-400">
                    <UiIcon name="info-circle" class="h-3.5 w-3.5" />
                  </span>
                </UiTooltip>
              </div>
              <UiUnitNumberField
                v-model="formAmount"
                v-model:unit="formCurrency"
                placeholder="0.00"
                :min="0"
                :step="0.01"
                :units="currencyChoices"
                name="quote-amount"
              />
            </div>
            <div v-if="requiresQuantityInput">
              <div class="mb-1 flex items-center gap-1.5">
                <UiText as="label" variant="p-small" :text="quantityLabel" class="text-gray-700" />
                <UiTooltip :content="quantityHelper">
                  <span class="inline-flex cursor-help text-gray-400">
                    <UiIcon name="info-circle" class="h-3.5 w-3.5" />
                  </span>
                </UiTooltip>
              </div>
              <UiInputField v-model="formQuantity" type="number" placeholder="1" />
            </div>
          </div>

          <div v-if="isFixedFee" class="mt-8">
            <UiCheckbox
              v-model="formHasFixedFeeQuantity"
              :label="quoteCopy.fixedFeeQuantityCheckbox"
              :description="quoteCopy.fixedFeeQuantityHelper"
            />
          </div>
        </div>

        <div v-if="isHoursBank">
          <div>
            <UiCheckbox v-model="formHasHoursBankExpiration" :label="quoteCopy.hoursBankExpirationCheckbox"
              :description="quoteCopy.hoursBankExpirationDescription" />
          </div>

          <div v-if="formHasHoursBankExpiration" class="grid grid-cols-1 gap-4">
            <div>
              <div class="mb-1 flex items-center gap-1.5">
                <UiText as="label" variant="p-small" :text="quoteCopy.hoursBankExpirationDaysLabel"
                  class="text-gray-700" />
                <UiTooltip :content="quoteCopy.hoursBankExpirationDaysHelper">
                  <span class="inline-flex cursor-help text-gray-400">
                    <UiIcon name="info-circle" class="h-3.5 w-3.5" />
                  </span>
                </UiTooltip>
              </div>
              <div class="flex items-center gap-2">
                <UiInputField v-model="formHoursBankExpiresAfterDays" type="number" placeholder="30" class="w-24" />
                <span class="text-sm text-gray-600">{{ quoteCopy.hoursBankExpirationDaysUnit }}</span>
              </div>
            </div>

            <div>
              <div class="mb-1 flex items-center gap-1.5">
                <UiText as="label" variant="p-small" :text="quoteCopy.hoursBankExpirationTermsLabel"
                  class="text-gray-700" />
                <UiTooltip :content="quoteCopy.hoursBankExpirationTermsHelper">
                  <span class="inline-flex cursor-help text-gray-400">
                    <UiIcon name="info-circle" class="h-3.5 w-3.5" />
                  </span>
                </UiTooltip>
              </div>
              <UiTextarea v-model="formHoursBankExpirationTerms" :rows="4"
                :placeholder="quoteCopy.hoursBankExpirationTermsPlaceholder" />
            </div>
          </div>
        </div>

        <div v-if="showDeliveryTimeline">
          <div class="mb-2 flex items-center gap-1.5">
            <UiText as="label" variant="p-small" :text="quoteCopy.deliveryTimelineLabel" class="text-gray-700" />
            <UiTooltip :content="quoteCopy.deliveryTimelineHelper">
              <span class="inline-flex cursor-help text-gray-400">
                <UiIcon name="info-circle" class="h-3.5 w-3.5" />
              </span>
            </UiTooltip>
          </div>
          <UiUnitNumberField
            v-model="formDeliveryAfterDays"
            v-model:unit="deliveryDayUnit"
            :placeholder="quoteCopy.deliveryTimelineDaysPlaceholder"
            :units="quoteCopy.deliveryTimelineDayUnits"
          />
        </div>

        <div v-if="isExternalPayment">
          <div class="mb-1 flex items-center gap-1.5">
            <UiText as="label" variant="p-small" :text="quoteCopy.externalPaymentLinkLabel" class="text-gray-700" />
            <UiTooltip :content="quoteCopy.externalPaymentLinkHelper">
              <span class="inline-flex cursor-help text-gray-400">
                <UiIcon name="info-circle" class="h-3.5 w-3.5" />
              </span>
            </UiTooltip>
          </div>
          <UiInputField v-model="formExternalPaymentUrl" type="text" placeholder="https://..." />
        </div>

        <div>
          <div class="mb-2 flex items-center gap-1.5">
            <UiText as="label" variant="p-small" :text="quoteCopy.scopeLabel" class="text-gray-700" />
            <UiTooltip :content="quoteCopy.scopeHelper">
              <span class="inline-flex cursor-help text-gray-400">
                <UiIcon name="info-circle" class="h-3.5 w-3.5" />
              </span>
            </UiTooltip>
          </div>
          <UiRichTextEditor v-model="formScope" :rows="7"
            :placeholder="quoteCopy.scopeHelper" />
        </div>

        <ProjectQuoteFeeEstimate
          v-if="!isExternalPayment"
          :acting-org-id="actingOrgId"
          :amount-cents="quoteTotalCents"
          :currency="selectedCurrency"
          :enabled="open"
        />
      </div>

      <div class="flex items-center justify-between">
        <UiButton variant="outline" label="Cancel" @click="closeAside" />
        <UiButton variant="primary" label="Send quote" :disabled="!canSubmit || isSubmitting" @click="handleSubmit" />
      </div>
    </div>
  </UiAside>
</template>

<style scoped>
:deep(input[type="number"]::-webkit-inner-spin-button),
:deep(input[type="number"]::-webkit-outer-spin-button) {
  -webkit-appearance: none;
  margin: 0;
}

:deep(input[type="number"]) {
  -moz-appearance: textfield;
  appearance: textfield;
}
</style>
